import { createStore } from '@xstate/store';
import type { CanvasStoreContext } from '../types';
import { generateLayerId } from '../types';
import { createDebouncedPersistence } from '../../stores/storePersistence';
import { commandRegistry } from '../commands/registry';
import { createCommandHistory } from '../commands/commandHistory';
import { createTransactionManager, type TransactionManager } from '../commands/transactionManager';
import type { CanvasEntity } from '../entityTypes';

// ============================================
// Canvas Store - Factory Pattern (per boardId)
// ============================================

function getLocalStorageKey(boardId: string): string {
  return `roadmap_2026_board_${boardId}_canvas`;
}

const EMPTY_STATE: CanvasStoreContext = {
  canvas: { id: '', name: '', boardId: '' },
  activeLayerId: '',
  layers: [],
  nodes: {},
  connectors: {},
  groups: {},
  nodesVersion: 0,
  connectorsVersion: 0,
  groupsVersion: 0,
};

/** Convert an array of entities to a Record keyed by id. */
function toRecord(arr: CanvasEntity[]): Record<string, CanvasEntity> {
  const rec: Record<string, CanvasEntity> = {};
  for (const e of arr) rec[e.id] = e;
  return rec;
}

/** Detect old format and migrate to new flat format. */
function migrateOldFormat(parsed: any): CanvasStoreContext {
  const trees = parsed.trees ?? [];
  const activeTreeId = parsed.activeTreeId ?? trees[0]?.id ?? '';
  const canvas = trees.find((t: any) => t.id === activeTreeId) ?? trees[0] ?? { id: '', name: 'Canvas', boardId: '' };

  let nodesArr: CanvasEntity[] = [];
  let connectorsArr: CanvasEntity[] = [];
  let groupsArr: CanvasEntity[] = [];

  if (parsed.entitiesById && !parsed.nodesById) {
    // Very old format: single entitiesById map
    const raw = parsed.entitiesById[activeTreeId] ?? [];
    if (Array.isArray(raw)) {
      let z = 0;
      const cleaned = raw
        .filter((e: any) => e.kind !== 'resource-badge')
        .map((e: any) => ({ ...e, zIndex: e.zIndex ?? z++ }));
      for (const e of cleaned) {
        if (e.kind === 'connector') connectorsArr.push(e);
        else if (e.kind === 'group') groupsArr.push(e);
        else nodesArr.push(e);
      }
    }
  } else {
    nodesArr = parsed.nodesById?.[activeTreeId] ?? [];
    connectorsArr = parsed.connectorsById?.[activeTreeId] ?? [];
    groupsArr = parsed.groupsById?.[activeTreeId] ?? [];

    // Filter out removed entity kinds and migrate missing zIndex
    if (Array.isArray(nodesArr)) {
      let z = 0;
      nodesArr = nodesArr
        .filter((e: any) => e.kind !== 'resource-badge')
        .map((e: any) => ({ ...e, zIndex: e.zIndex ?? z++ }));
    }
  }

  return {
    canvas,
    activeLayerId: parsed.activeLayerIdByTreeId?.[activeTreeId] ?? '',
    layers: parsed.layersById?.[activeTreeId] ?? [],
    nodes: toRecord(nodesArr),
    connectors: toRecord(connectorsArr),
    groups: toRecord(groupsArr),
    nodesVersion: 0,
    connectorsVersion: 0,
    groupsVersion: 0,
  };
}

function loadFromLocalStorage(storageKey: string): CanvasStoreContext {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return { ...EMPTY_STATE };

    const parsed = JSON.parse(saved);

    // New flat format detection: has 'canvas' key
    if (parsed.canvas) {
      return {
        canvas: parsed.canvas,
        activeLayerId: parsed.activeLayerId ?? '',
        layers: parsed.layers ?? [],
        nodes: parsed.nodes ?? {},
        connectors: parsed.connectors ?? {},
        groups: parsed.groups ?? {},
        nodesVersion: parsed.nodesVersion ?? 0,
        connectorsVersion: parsed.connectorsVersion ?? 0,
        groupsVersion: parsed.groupsVersion ?? 0,
      };
    }

    // Old format: has 'trees' array
    if (Array.isArray(parsed.trees)) {
      return migrateOldFormat(parsed);
    }

    return { ...EMPTY_STATE };
  } catch {
    return { ...EMPTY_STATE };
  }
}

// ── Store Performance Profiler (sampled) ──────────────────────────
// Enable:  __CANVAS_STORE_PERF__ = true   (browser console)
// Disable: __CANVAS_STORE_PERF__ = false
// Prints a summary table every 2s sorted by total time (most expensive first).

interface PerfStats { calls: number; totalMs: number; maxMs: number }

// Send stats: sync = reducer + subscriber notifications, micro = + microtask selectors/renders
const perfSendSync = new Map<string, PerfStats>();
const perfSendMicro = new Map<string, PerfStats>();
let perfTimer: ReturnType<typeof setInterval> | null = null;

function formatMs(ms: number): string {
  if (ms >= 1) return ms.toFixed(2) + 'ms';
  return (ms * 1000).toFixed(0) + '\u00b5s';
}

function addStat(map: Map<string, PerfStats>, key: string, elapsed: number) {
  let s = map.get(key);
  if (!s) { s = { calls: 0, totalMs: 0, maxMs: 0 }; map.set(key, s); }
  s.calls++;
  s.totalMs += elapsed;
  if (elapsed > s.maxMs) s.maxMs = elapsed;
}

function printTable(label: string, color: string, map: Map<string, PerfStats>) {
  const rows: Array<[string, PerfStats]> = [];
  for (const [type, s] of map) {
    if (s.calls === 0) continue;
    rows.push([type, { ...s }]);
    s.calls = 0; s.totalMs = 0; s.maxMs = 0;
  }
  if (rows.length === 0) return;
  rows.sort((a, b) => b[1].totalMs - a[1].totalMs);
  const table: Record<string, { calls: number; total: string; max: string; avg: string }> = {};
  for (const [type, s] of rows) {
    table[type] = { calls: s.calls, total: formatMs(s.totalMs), max: formatMs(s.maxMs), avg: formatMs(s.totalMs / s.calls) };
  }
  console.log(`%c${label}`, `color: ${color}; font-weight: bold`);
  console.table(table);
}

function ensurePerfTimer() {
  if (perfTimer) return;
  perfTimer = setInterval(() => {
    if (!(globalThis as any).__CANVAS_STORE_PERF__) return;
    printTable('[store-perf] send sync (2s)', '#f97316', perfSendSync);
    printTable('[store-perf] send sync+micro (2s)', '#ef4444', perfSendMicro);
  }, 2000);
}

function createCanvasStoreBundle(storageKey: string, boardId: string) {
  let initialState = loadFromLocalStorage(storageKey);

  // Initialize canvas on first load if empty
  if (!initialState.canvas.id) {
    const defaultLayerId = generateLayerId();
    initialState = {
      canvas: { id: `canvas-${Date.now()}`, name: 'Canvas', boardId },
      activeLayerId: defaultLayerId,
      layers: [{ id: defaultLayerId, name: 'Default', visible: true, locked: false }],
      nodes: {},
      connectors: {},
      groups: {},
      nodesVersion: 0,
      connectorsVersion: 0,
      groupsVersion: 0,
    };
  }

  // Ensure at least one layer exists
  if (initialState.layers.length === 0) {
    const defaultLayerId = generateLayerId();
    initialState = {
      ...initialState,
      activeLayerId: defaultLayerId,
      layers: [{ id: defaultLayerId, name: 'Default', visible: true, locked: false }],
    };
  }

  const store = createStore({
    context: initialState,

    on: {
      // Command-Handlers = Reducers, now from entity-specific command files
      ...commandRegistry,

      // Internal restore for Undo/Redo (not a command, internal only)
      'state.restore': (_ctx: CanvasStoreContext, event: { context: CanvasStoreContext }) => {
        return event.context;
      },
    },
  });

  // Debounced persistence — hash via cheap counter (immutable context = new ref per send)
  let persistenceVersion = 0;
  const persistence = createDebouncedPersistence<CanvasStoreContext>(
    storageKey,
    (ctx) => JSON.stringify(ctx),
    () => String(++persistenceVersion),
  );

  store.subscribe((snapshot) => {
    persistence.save(snapshot.context);
  });

  // CommandHistory + TransactionManager wrap store.send
  const commandHistory = createCommandHistory();
  const originalSend = store.send.bind(store);
  const txManager = createTransactionManager(store, originalSend, commandHistory);

  // Redirect store.send() through txManager.dispatch()
  // -> All 200+ existing store.send() callsites automatically go through the command system
  store.send = ((event: { type: string; [key: string]: unknown }) => {
    if (!(globalThis as any).__CANVAS_STORE_PERF__) {
      txManager.dispatch(event as any);
      return;
    }
    ensurePerfTimer();
    const type = event.type;
    const t0 = performance.now();
    performance.mark(`send(${type}):s`);
    txManager.dispatch(event as any);
    const syncElapsed = performance.now() - t0;
    performance.mark(`send(${type}):sync`);
    addStat(perfSendSync, type, syncElapsed);
    // Measure again after microtasks flush (where React evaluates selectors + re-renders)
    Promise.resolve().then(() => {
      const totalElapsed = performance.now() - t0;
      performance.mark(`send(${type}):micro`);
      try { performance.measure(`${type} (sync)`, `send(${type}):s`, `send(${type}):sync`); } catch {}
      try { performance.measure(`${type} (sync+micro)`, `send(${type}):s`, `send(${type}):micro`); } catch {}
      addStat(perfSendMicro, type, totalElapsed);
    });
  }) as typeof store.send;

  return { store, txManager };
}

// ============================================
// Store Instances (per boardId)
// ============================================

type CanvasStoreBundle = ReturnType<typeof createCanvasStoreBundle>;

const storeInstances = new Map<string, CanvasStoreBundle>();

export function initCanvasStore(boardId: string): CanvasStoreBundle {
  const existing = storeInstances.get(boardId);
  if (existing) return existing;

  const storageKey = getLocalStorageKey(boardId);
  const bundle = createCanvasStoreBundle(storageKey, boardId);
  storeInstances.set(boardId, bundle);
  return bundle;
}

export function getCanvasStore(boardId: string): CanvasStore {
  const bundle = storeInstances.get(boardId);
  if (!bundle) {
    throw new Error(`Canvas store for board "${boardId}" not initialized. Call initCanvasStore first.`);
  }
  return bundle.store;
}

export function getTransactionManager(boardId: string): TransactionManager {
  const bundle = storeInstances.get(boardId);
  if (!bundle) {
    throw new Error(`Canvas store for board "${boardId}" not initialized. Call initCanvasStore first.`);
  }
  return bundle.txManager;
}

export type CanvasStore = CanvasStoreBundle['store'];
